/**
 * `/sign-in` — Tarii's own sign-in page (3 Oct 2026), built on Clerk's
 * headless `useSignIn()` hook. Clerk keeps doing the authenticating;
 * nothing about the session, the backend or what happens after sign-in
 * changes. The prebuilt Clerk card is one release away behind
 * `?classic=1` (see `SignInGate` in `App.tsx`).
 *
 * Page protocol (founder, 21 Sep):
 *   - Why the user comes: to get to their roster — usually on a phone,
 *     often from a link in an email (an invite, a published roster, a
 *     change), sometimes after being signed out.
 *   - What they see first: one obvious way in — the Tarii mark, one line,
 *     Continue with Google, then the email form.
 *   - What they adjust immediately: they type their email (or tap
 *     Google). Everything else — a code instead of a password, a
 *     forgotten password, creating an account — is a quiet text link.
 *
 * Flows (Clerk legacy/Core 2 custom-flow API, which is what
 * `@clerk/clerk-react` 5.x ships):
 *   - Google → `authenticateWithRedirect` → `/sso-callback`.
 *   - Email + password → `signIn.create({ identifier, password })`.
 *   - Email me a code → `create({ identifier })` → `prepareFirstFactor`
 *     (email_code) → `attemptFirstFactor`.
 *   - Forgot password → `create({ strategy: reset_password_email_code })`
 *     → `attemptFirstFactor` (code) → `resetPassword` (new password).
 *   - Every status the docs list is routed by `advance()`:
 *     `complete`; `needs_second_factor` (Clerk's Client/Device Trust asks
 *     for an emailed code on a new device even with MFA off);
 *     `needs_first_factor`; `needs_new_password`.
 *
 * After `setActive` this page does not navigate: `SignInGate` sees the
 * session through `AuthContext` and sends the person on exactly as it
 * does for anyone already signed in (pending invite → its accept page,
 * otherwise `/`, where the existing entry gates take over).
 *
 * #3005 — `mode="add"` is **Add another account** (`/sign-in/add`): the
 * same form for a person already signed in, with no create-account link.
 * It ends in the account switch (`runAccountSwitch`: clear the caches,
 * `setActive`, clear again, a full load of `/`). Google first drops the
 * caches and the workspace pointer, then returns through `/sso-callback`
 * as a full load. While Clerk runs in single-session mode, Clerk refuses
 * the second sign-in with `session_exists`; the page then offers
 * **Sign out and sign in as another account**.
 */
import { useClerk, useSignIn } from "@clerk/clerk-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../lib/AuthContext";
import {
  CHOOSE_ACCOUNT_PATH,
  markAccountAddPending,
  prepareAccountChange,
  runAccountSwitch,
} from "../../lib/accountSwitch";

import {
  AUTH_LEAD_LINE,
  AlreadySignedIn,
  AuthAlert,
  AuthField,
  AuthFrame,
  AuthNotice,
  GoogleButton,
  OrDivider,
  QuietAction,
  SubmitButton,
  quietLinkClass,
} from "./AuthFrame";
import { type AuthErrorFlow, type FriendlyAuthError, describeClerkError } from "./clerkErrors";

type SignInResource = NonNullable<ReturnType<typeof useSignIn>["signIn"]>;
type FirstFactor = NonNullable<SignInResource["supportedFirstFactors"]>[number];
type SecondFactor = NonNullable<SignInResource["supportedSecondFactors"]>[number];
type EmailCodeFirst = Extract<FirstFactor, { strategy: "email_code" }>;
type EmailCodeSecond = Extract<SecondFactor, { strategy: "email_code" }>;
type ResetFactor = Extract<FirstFactor, { strategy: "reset_password_email_code" }>;

type Step =
  | { kind: "start" }
  | { kind: "code"; purpose: "first-factor" | "second-factor"; sentTo: string }
  | { kind: "forgot" }
  | { kind: "reset"; sentTo: string }
  | { kind: "new-password" }
  | { kind: "signed-in" };

type Busy =
  | null
  | "google"
  | "password"
  | "code-request"
  | "verify"
  | "resend"
  | "reset-request"
  | "reset"
  | "new-password"
  | "finishing";

function isEmailCodeFirst(f: FirstFactor): f is EmailCodeFirst {
  return f.strategy === "email_code";
}
function isEmailCodeSecond(f: SecondFactor): f is EmailCodeSecond {
  return f.strategy === "email_code";
}
function isResetFactor(f: FirstFactor): f is ResetFactor {
  return f.strategy === "reset_password_email_code";
}

/** Sentence for a step we cannot drive here (e.g. an authenticator app). */
const UNSUPPORTED: FriendlyAuthError = {
  message:
    "This account needs a check this page can't do yet. Use the classic sign-in page to finish.",
  code: "unsupported_factor",
};

/** #3005 — the add page's head and its way out in single-session mode. */
export const ADD_ACCOUNT_COPY = {
  title: "Add another account",
  lead: "Sign in to the other account. This one stays signed in on this browser.",
  signOutFirst: "Sign out and sign in as another account",
} as const;

export function SignInPage({
  resume = false,
  mode = "sign-in",
}: {
  resume?: boolean;
  /** #3005 — `add`: Add another account, for a person already signed in. */
  mode?: "sign-in" | "add";
}) {
  const adding = mode === "add";
  const signInFlow: AuthErrorFlow = adding ? "add" : "sign-in";
  const { isLoaded, signIn, setActive } = useSignIn();
  const clerk = useClerk();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { email?: unknown } | null)?.email;

  const [step, setStep] = useState<Step>(() =>
    clerk.isSignedIn && !adding ? { kind: "signed-in" } : { kind: "start" },
  );
  const [email, setEmail] = useState(typeof prefill === "string" ? prefill : "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<FriendlyAuthError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  const ready = isLoaded && Boolean(signIn) && Boolean(setActive);
  const locked = busy !== null;

  function go(next: Step) {
    setStep(next);
    setCode("");
    setNotice(null);
  }

  function fail(err: unknown, flow: AuthErrorFlow) {
    const friendly = describeClerkError(err, flow);
    if (friendly.action === "signed-in") {
      go({ kind: "signed-in" });
      setError(null);
      return;
    }
    setError(friendly);
  }

  async function finish(res: SignInResource) {
    if (!setActive) return;
    setBusy("finishing");
    const activate = () =>
      setActive({
        session: res.createdSessionId,
        navigate: async ({ session }) => {
          // A pending session task (an org choice, a forced password reset)
          // is finished on Clerk's task screen under /sign-in/tasks. With
          // no task there is nothing to do here — see the header comment.
          if (session?.currentTask) await clerk.redirectToTasks();
        },
      });
    // #3005 — a second account: the switch steps, ending in a full load.
    if (adding) {
      await runAccountSwitch({ label: email.trim() || "the other account", activate });
      return;
    }
    await activate();
  }

  /** Route any sign-in state to the step that finishes it. */
  async function advance(res: SignInResource) {
    switch (res.status) {
      case "complete":
        await finish(res);
        return;
      case "needs_second_factor": {
        // Client Trust: a password sign-in from a browser Clerk hasn't
        // seen is asked for a code emailed to the account.
        const factor = res.supportedSecondFactors?.find(isEmailCodeSecond);
        if (!factor) {
          setError(UNSUPPORTED);
          return;
        }
        await res.prepareSecondFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
        go({ kind: "code", purpose: "second-factor", sentTo: factor.safeIdentifier || email });
        return;
      }
      case "needs_first_factor": {
        const factor = res.supportedFirstFactors?.find(isEmailCodeFirst);
        if (!factor) {
          setError(UNSUPPORTED);
          return;
        }
        await res.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
        go({ kind: "code", purpose: "first-factor", sentTo: factor.safeIdentifier || email });
        return;
      }
      case "needs_new_password":
        go({ kind: "new-password" });
        return;
      default:
        go({ kind: "start" });
        setError({ message: "This attempt timed out. Start again from the top.", code: null });
    }
  }

  /**
   * Run one Clerk call with the page locked. On success a `finishing`
   * lock (and, with `hold`, the step's own lock — Google is leaving the
   * page) stays until SignInGate swaps this page out; any failure
   * frees the page again and shows the sentence.
   */
  async function run(
    kind: Exclude<Busy, null>,
    flow: AuthErrorFlow,
    task: () => Promise<void>,
    hold = false,
  ) {
    setBusy(kind);
    setError(null);
    setNotice(null);
    let ok = false;
    try {
      await task();
      ok = true;
    } catch (err) {
      fail(err, flow);
    } finally {
      setBusy((b) => (ok && (b === "finishing" || hold) ? b : null));
    }
  }

  // Arrived from `/sso-callback` (`/sign-in/continue`): pick up the
  // attempt Clerk left on the client. Arrived fresh: surface a Google
  // error the callback sent back here, then clear it the way Clerk's own
  // card does so a reload doesn't show it again.
  const mounted = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once, when Clerk is ready.
  useEffect(() => {
    if (!ready || !signIn || mounted.current) return;
    mounted.current = true;
    if (step.kind === "signed-in") return;
    if (resume) {
      const pending = ["needs_first_factor", "needs_second_factor", "needs_new_password"];
      if (signIn.status && pending.includes(signIn.status)) {
        if (signIn.identifier) setEmail(signIn.identifier);
        void run("code-request", "sign-in", () => advance(signIn));
      } else {
        navigate("/sign-in", { replace: true });
      }
      return;
    }
    const oauthError = signIn.firstFactorVerification?.error;
    if (oauthError) {
      fail({ errors: [oauthError] }, "sign-in");
      void signIn.create({}).catch(() => undefined);
    }
  }, [ready]);

  useEffect(() => {
    if (step.kind === "code") codeRef.current?.focus();
  }, [step]);

  // ── handlers ──────────────────────────────────────────────────────

  function onGoogle() {
    if (!signIn) return;
    void run(
      "google",
      signInFlow,
      async () => {
        // #3005 — leaving to sign in as another account: nothing of this
        // one rides along to the return (`/sso-callback` is a full load).
        // The mark lets the new account tell the other tabs once it loads.
        if (adding) {
          markAccountAddPending(auth.accounts?.find((a) => a.current)?.key ?? null);
          await prepareAccountChange();
        }
        await signIn.authenticateWithRedirect({
          strategy: "oauth_google",
          redirectUrl: "/sso-callback",
          redirectUrlComplete: "/",
        });
      },
      true,
    );
  }

  /** #3005 — single-session Clerk: today's path, sign out and sign in again. */
  async function signOutForAnother() {
    setBusy("finishing");
    try {
      await auth.signOut({ redirectUrl: "/sign-in", scope: "all" });
    } catch (err) {
      console.error("Sign-out failed to revoke the session", err);
    } finally {
      window.location.assign("/sign-in");
    }
  }

  function needEmail(): boolean {
    if (email.trim()) return false;
    setError({ message: "Enter your email address first.", code: "form_param_nil" });
    emailRef.current?.focus();
    return true;
  }

  function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn || needEmail()) return;
    if (!password) {
      setError({
        message: "Enter your password, or choose Email me a code instead.",
        code: "form_param_nil",
      });
      passwordRef.current?.focus();
      return;
    }
    void run("password", signInFlow, async () => {
      const res = await signIn.create({ identifier: email.trim(), password });
      await advance(res);
    });
  }

  function onEmailCode() {
    if (!signIn || needEmail()) return;
    void run("code-request", signInFlow, async () => {
      const res = await signIn.create({ identifier: email.trim() });
      await advance(res);
    });
  }

  function verify(value: string) {
    if (!signIn || step.kind !== "code") return;
    const purpose = step.purpose;
    void run("verify", "code", async () => {
      const res =
        purpose === "second-factor"
          ? await signIn.attemptSecondFactor({ strategy: "email_code", code: value })
          : await signIn.attemptFirstFactor({ strategy: "email_code", code: value });
      await advance(res);
    });
  }

  function onCodeSubmit(e: FormEvent) {
    e.preventDefault();
    if (code.length < 6) {
      setError({ message: "Enter the 6-digit code from the email.", code: "form_param_nil" });
      codeRef.current?.focus();
      return;
    }
    verify(code);
  }

  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    // Six digits typed or pasted: verify straight away, as a phone
    // keyboard's one-time-code suggestion expects.
    if (digits.length === 6 && !locked) verify(digits);
  }

  function onResend() {
    if (!signIn) return;
    void run("resend", "code", async () => {
      if (step.kind === "code" && step.purpose === "second-factor") {
        const factor = signIn.supportedSecondFactors?.find(isEmailCodeSecond);
        if (!factor) throw new Error("no email code factor");
        await signIn.prepareSecondFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
      } else if (step.kind === "code") {
        const factor = signIn.supportedFirstFactors?.find(isEmailCodeFirst);
        if (!factor) throw new Error("no email code factor");
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
      } else if (step.kind === "reset") {
        const factor = signIn.supportedFirstFactors?.find(isResetFactor);
        if (factor) {
          await signIn.prepareFirstFactor({
            strategy: "reset_password_email_code",
            emailAddressId: factor.emailAddressId,
          });
        } else {
          await signIn.create({ strategy: "reset_password_email_code", identifier: email.trim() });
        }
      }
      setCode("");
      setNotice("We sent a new code. It replaces the one before it.");
    });
  }

  function onForgotSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn || needEmail()) return;
    void run("reset-request", "sign-in", async () => {
      const res = await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email.trim(),
      });
      const factor = res.supportedFirstFactors?.find(isResetFactor);
      go({ kind: "reset", sentTo: factor?.safeIdentifier || email.trim() });
    });
  }

  function onResetSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    const codeAccepted = signIn.status === "needs_new_password";
    if (!codeAccepted && code.length < 6) {
      setError({ message: "Enter the 6-digit code from the email.", code: "form_param_nil" });
      return;
    }
    if (!newPassword) {
      setError({ message: "Choose a new password.", code: "form_param_nil" });
      return;
    }
    void run("reset", "reset", async () => {
      let res: SignInResource = signIn;
      if (!codeAccepted) {
        try {
          res = await signIn.attemptFirstFactor({ strategy: "reset_password_email_code", code });
        } catch (err) {
          fail(err, "code");
          return;
        }
      }
      if (res.status === "needs_new_password") {
        res = await signIn.resetPassword({ password: newPassword, signOutOfOtherSessions: true });
      }
      await advance(res);
    });
  }

  function onNewPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    if (!newPassword) {
      setError({ message: "Choose a new password.", code: "form_param_nil" });
      return;
    }
    void run("new-password", "reset", async () => {
      const res = await signIn.resetPassword({
        password: newPassword,
        signOutOfOtherSessions: true,
      });
      await advance(res);
    });
  }

  function backToStart() {
    go({ kind: "start" });
    setError(null);
  }

  // ── error follow-ups ──────────────────────────────────────────────

  function errorActions(err: FriendlyAuthError) {
    switch (err.action) {
      case "sign-up":
        return (
          <Link to="/sign-up" state={{ email: email.trim() }} className={quietLinkClass}>
            Create an account
          </Link>
        );
      case "reset-password":
        return (
          <QuietAction
            onClick={() => {
              go({ kind: "forgot" });
              setError(null);
            }}
          >
            Reset your password
          </QuietAction>
        );
      case "resend-code":
        return (
          <QuietAction onClick={onResend} disabled={locked}>
            Send a new code
          </QuietAction>
        );
      case "sign-out-first":
        return (
          <QuietAction onClick={() => void signOutForAnother()} disabled={locked}>
            {ADD_ACCOUNT_COPY.signOutFirst}
          </QuietAction>
        );
      case "choose-account":
        return (
          <Link to={CHOOSE_ACCOUNT_PATH} className={quietLinkClass}>
            Switch accounts
          </Link>
        );
      default:
        return undefined;
    }
  }

  const alert = error ? <AuthAlert error={error} actions={errorActions(error)} /> : null;

  // ── steps ─────────────────────────────────────────────────────────

  if (step.kind === "signed-in") {
    return (
      <AuthFrame title="You're already signed in" lead="This browser is signed in to Tarii.">
        <AlreadySignedIn />
      </AuthFrame>
    );
  }

  if (step.kind === "code") {
    const secondFactor = step.purpose === "second-factor";
    return (
      <AuthFrame
        title={secondFactor ? "Confirm it's you" : "Check your email"}
        lead={
          secondFactor ? (
            <>
              This browser is new to your account, so we emailed a 6-digit code to{" "}
              <span className="font-medium text-fg">{step.sentTo}</span>.
            </>
          ) : (
            <>
              We sent a 6-digit code to <span className="font-medium text-fg">{step.sentTo}</span>.
            </>
          )
        }
      >
        <form onSubmit={onCodeSubmit} noValidate className="flex flex-col gap-4">
          <AuthField
            ref={codeRef}
            id="sign-in-code"
            label="Code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            disabled={locked}
            className="auth-code-input font-mono"
          />
          {alert}
          {notice ? <AuthNotice>{notice}</AuthNotice> : null}
          <SubmitButton
            busy={busy === "verify" || busy === "finishing"}
            busyLabel="Signing you in…"
            disabled={!ready || locked}
          >
            Continue
          </SubmitButton>
        </form>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
          <QuietAction onClick={onResend} disabled={locked}>
            {busy === "resend" ? "Sending…" : "Send a new code"}
          </QuietAction>
          <QuietAction onClick={backToStart} disabled={locked}>
            Use a different email
          </QuietAction>
        </div>
      </AuthFrame>
    );
  }

  if (step.kind === "forgot") {
    return (
      <AuthFrame
        title="Reset your password"
        lead="We'll email you a code. You'll choose a new password on the next screen."
      >
        <form onSubmit={onForgotSubmit} noValidate className="flex flex-col gap-4">
          <AuthField
            ref={emailRef}
            id="sign-in-reset-email"
            label="Email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={locked}
          />
          {alert}
          <SubmitButton
            busy={busy === "reset-request"}
            busyLabel="Sending…"
            disabled={!ready || locked}
          >
            Email me a reset code
          </SubmitButton>
        </form>
        <div className="mt-5">
          <QuietAction onClick={backToStart} disabled={locked}>
            Back to sign in
          </QuietAction>
        </div>
      </AuthFrame>
    );
  }

  if (step.kind === "reset" || step.kind === "new-password") {
    const withCode = step.kind === "reset" && signIn?.status !== "needs_new_password";
    return (
      <AuthFrame
        title="Choose a new password"
        lead={
          step.kind === "reset" ? (
            <>
              We sent a 6-digit code to <span className="font-medium text-fg">{step.sentTo}</span>.
              Enter it with your new password.
            </>
          ) : (
            "Your password needs replacing before you can carry on."
          )
        }
      >
        <form
          onSubmit={step.kind === "reset" ? onResetSubmit : onNewPasswordSubmit}
          noValidate
          className="flex flex-col gap-4"
        >
          {withCode ? (
            <AuthField
              id="sign-in-reset-code"
              label="Code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={locked}
              className="auth-code-input font-mono"
            />
          ) : null}
          {/* The account's email, for password managers saving the new password. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            hidden
          />
          <AuthField
            id="sign-in-new-password"
            label="New password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={locked}
          />
          <p className="-mt-2 text-fg-subtle text-xs">
            At least 8 characters. You'll be signed out on other devices.
          </p>
          {alert}
          {notice ? <AuthNotice>{notice}</AuthNotice> : null}
          <SubmitButton
            busy={busy === "reset" || busy === "new-password" || busy === "finishing"}
            busyLabel="Saving…"
            disabled={!ready || locked}
          >
            Save and sign in
          </SubmitButton>
        </form>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
          {withCode ? (
            <QuietAction onClick={onResend} disabled={locked}>
              {busy === "resend" ? "Sending…" : "Send a new code"}
            </QuietAction>
          ) : null}
          <QuietAction onClick={backToStart} disabled={locked}>
            Back to sign in
          </QuietAction>
        </div>
      </AuthFrame>
    );
  }

  // ── start ─────────────────────────────────────────────────────────
  return (
    <AuthFrame
      title={adding ? ADD_ACCOUNT_COPY.title : "Sign in to Tarii"}
      lead={adding ? ADD_ACCOUNT_COPY.lead : AUTH_LEAD_LINE}
      footer={
        // #3005 — sign-up while signed in is out of scope: no link on Add.
        adding ? undefined : (
          <>
            New to Tarii?{" "}
            <Link to="/sign-up" state={{ email: email.trim() }} className={quietLinkClass}>
              Create an account
            </Link>
          </>
        )
      }
    >
      <GoogleButton onClick={onGoogle} busy={busy === "google"} disabled={!ready || locked} />
      <OrDivider />
      <form onSubmit={onPasswordSubmit} noValidate className="flex flex-col gap-4">
        <AuthField
          ref={emailRef}
          id="sign-in-email"
          label="Email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={locked}
        />
        <AuthField
          ref={passwordRef}
          id="sign-in-password"
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={locked}
        />
        {alert}
        <SubmitButton
          busy={busy === "password" || busy === "finishing"}
          busyLabel="Signing in…"
          disabled={!ready || locked}
        >
          Sign in
        </SubmitButton>
      </form>
      {/* Secondary paths after the primary action, so Tab runs email →
          password → Sign in before reaching them. */}
      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
        <QuietAction onClick={onEmailCode} disabled={!ready || locked}>
          {busy === "code-request" ? "Sending a code…" : "Email me a code instead"}
        </QuietAction>
        <QuietAction
          onClick={() => {
            go({ kind: "forgot" });
            setError(null);
          }}
          disabled={locked}
        >
          Forgot password?
        </QuietAction>
      </div>
    </AuthFrame>
  );
}
